import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { pollQuestionResponsesApiThunk } from 'src/store/questionResponses/questionResponsesSlice';
import { POLL_INTERVAL_MS } from 'src/contstant/commonLabels';

// Random jitter (0-3s) added to each interval so concurrent clients don't
// synchronize their requests and create periodic load spikes on the backend.
const POLL_JITTER_MS = 3000;

// Stop polling after this many consecutive failures to avoid hammering a
// backend that is consistently erroring.
const MAX_CONSECUTIVE_ERRORS = 5;

interface UseQuestionResponsePollerProps {
  auditId: string;
  businessUnitId: string;
  enabled: boolean;
}

/**
 * Polls question responses on a jittered ~15s cadence using a silent thunk that
 * only updates entries whose lastUpdatedAt has changed. Does not clear state or
 * trigger loading spinners.
 *
 * Pauses when:
 * - Feature flag is off (enabled = false)
 * - A response update, media upload, or media delete is in flight
 * - The browser tab is not visible
 *
 * Stops after MAX_CONSECUTIVE_ERRORS consecutive failures to avoid hammering
 * a failing backend; the error count resets on any successful poll.
 */
export const useQuestionResponsePoller = ({
  auditId,
  businessUnitId,
  enabled,
}: UseQuestionResponsePollerProps) => {
  const dispatch = useAppDispatch();

  // Any in-flight operation that could conflict with a background merge
  const isBusy = useAppSelector(
    (state) =>
      state.questionResponses.updateQuestionResponseApiStatus.isLoading ||
      state.media.uploadMediaApiStatus.isLoading ||
      state.media.deleteMediaApiStatus.isLoading,
  );

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBusyRef = useRef(isBusy);
  const errorCountRef = useRef(0);

  // Keep the busy flag current for the scheduled callback
  useEffect(() => {
    isBusyRef.current = isBusy;
  }, [isBusy]);

  useEffect(() => {
    if (!enabled || !auditId || !businessUnitId) return;

    let stopped = false;
    errorCountRef.current = 0;

    const scheduleNext = () => {
      if (stopped) return;
      const delay = POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS;
      timeoutRef.current = setTimeout(poll, delay);
    };

    const poll = async () => {
      // Skip this cycle if busy or the tab is hidden, but keep the loop alive
      if (isBusyRef.current || document.hidden) {
        scheduleNext();
        return;
      }

      const resultAction = await dispatch(
        pollQuestionResponsesApiThunk({ auditId, businessUnitId }),
      );

      if (stopped) return;

      if (pollQuestionResponsesApiThunk.rejected.match(resultAction)) {
        errorCountRef.current += 1;
        if (errorCountRef.current >= MAX_CONSECUTIVE_ERRORS) {
          // Give up to avoid hammering a consistently failing backend
          return;
        }
      } else {
        errorCountRef.current = 0;
      }

      scheduleNext();
    };

    scheduleNext();

    return () => {
      stopped = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [enabled, auditId, businessUnitId, dispatch]);
};
